import React, { FC, useState } from 'react'
import { SearchIcon } from 'lucide-react'
import { where } from 'firebase/firestore'
import { uniq, uniqBy } from 'lodash'

import { Label } from '../../ui/label'
import { Checkbox } from '../../ui/checkbox'
import { Input } from '../../ui/input'
import ViewsLoader from '../../loader/ViewsLoader'
import customImageComponent from '../../common/CustomImageComponent'

import FirestoreService from '../../../services/firestoreService'
import EventRegisteredUsersService from '../../../services/firebase-service/event-registered-users/EventRegisteredUsersService'
import HorsesService from '../../../services/firebase-service/horses/HorsesService'

import { IHorseData } from '../../../models/horse/horse.interface'
import { HorseModel } from '../../../models/horse/horse.model'

import { CONST } from '../../../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

interface SelectHorseProps {
  horses: (IHorseData & { selected?: boolean })[]
  setHorses: (value: (IHorseData & { selected?: boolean })[]) => void
  registrationDocId: string
}

export const SelectHorse: FC<SelectHorseProps> = ({ horses, setHorses, registrationDocId }) => {
  const [searchValue, setSearchValue] = useState('')
  const [loading, setLoading] = useState(false)

  const onCheckedChange = (id: string, value: boolean) => {
    const horsesUpdated = horses.map((item) => {
      if (item.id === id) {
        return { ...item, selected: value || false }
      } else {
        return item
      }
    })
    setHorses([...horsesUpdated])
  }

  const onSearch = async (strToSearch: string) => {
    const search = strToSearch.toLowerCase().replace(/\s+/g, '')
    setSearchValue(search)

    try {
      setLoading(true)

      const registrationInDb = await EventRegisteredUsersService.getById(registrationDocId)
      const registrationHorsesInDb = await HorsesService.getHorsesByIds(
        uniq(registrationInDb.horsesIds)
      )

      if (search.length < 3) {
        setHorses(
          uniqBy([...horses.filter((horse) => horse.selected), ...registrationHorsesInDb], 'id')
        )
        return
      }

      const horsesInDb: IHorseData[] = []
      const horsesSnaps = await FirestoreService.filterItems(COLLECTIONS.HORSES.NAME, [
        where(COLLECTIONS.HORSES.FIELDS.HORSE_NAME_N_GRAM.KEY, 'array-contains', search),
      ])
      horsesSnaps.forEach((currDoc) => {
        const horse = HorseModel.fromFirestoreDoc(currDoc).toObject()
        horsesInDb.push(horse)
      })

      setHorses(
        uniqBy(
          [
            ...horses.filter((horse) => horse.selected),
            ...registrationHorsesInDb.filter((horse) =>
              horse.horseName?.toLowerCase().includes(searchValue.toLowerCase())
            ),
            ...horsesInDb,
          ],
          'id'
        )
      )
    } catch (error) {
      console.error('error: ', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="flex gap-2 justify-between items-center mb-4">
        <h4 className="text-lg font-normal">Select horse</h4>
      </div>
      <div className="inputSearch mb-5 flex items-center relative">
        <SearchIcon className="absolute text-black/30 left-4 w-4" />
        <Input
          type="text"
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search horse name"
          className="rounded-full px-4 py-3 pl-10 h-auto text-base placeholder:text-black/20"
        />
      </div>
      {loading ? (
        <div className="flex justify-center items-center w-full h-full">
          <ViewsLoader size="xl" color="red" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {horses?.[0] ? (
            horses.map((horse) => (
              <div key={horse.id} className="w-full flex gap-3">
                <Label className="relative flex-wrap font-normal cursor-pointer flex-1 pr-8">
                  {customImageComponent(
                    horse.horseProfilePicture,
                    horse.horseName,
                    '!w-[45px] !min-w-[45px] !h-[45px] rounded-full border border-black/10'
                  )}
                  <div>
                    <h4 className="text-base">{horse.horseName}</h4>
                    <p className="mt-1 text-xs text-black/50">{horse.horseOwnerName}</p>
                  </div>
                  <Checkbox
                    checked={horse.selected || false}
                    className="absolute z-10 right-0 top-1/2 -translate-y-1/2 rounded-full w-5 h-5 border-black/20"
                    onCheckedChange={(value) =>
                      horse.id && onCheckedChange(horse.id, value as boolean)
                    }
                  />
                </Label>
              </div>
            ))
          ) : (
            <p className="mt-2 text-center text-black/50">
              {searchValue.length < 3
                ? 'Please enter at least 3 letters in the search'
                : 'No result found.'}
            </p>
          )}
        </div>
      )}
    </>
  )
}
